import * as React from "react";
import { ReactNode } from "react";
import { Accordion, Box, Heading, Text } from "@origin-digital/ods-core";
import { styled } from "@mui/material";
import FormContext, {
  ElectricityService,
  GasService,
  InternetService,
} from "src/context/FormContext";
import { pageHeaderHeight } from "src/constants";
import {
  RequestStatusAssignedIcon,
  RequestStatusCancelledIcon,
  RequestStatusCompletedIcon,
  RequestStatusDraftIcon,
  RequestStatusFinishedIcon,
  RequestStatusInProgressIcon,
  RequestStatusPausedIcon,
} from "../Premises/Icons";
import { RequestStatusEnum } from "../Premises/types";
import { getStatusIconString, getStatusText } from "../Premises/helpers";
import { fromHyphenatedToCamelCase } from "./helper";
import { useResponsiveBelowMd } from "src/hooks";

interface FormSectionProps {
  id: string;
  title: string;
  service?: ElectricityService | GasService | InternetService;
  visible?: boolean;
  children: ReactNode;
  outerRef: React.RefObject<HTMLDivElement>;
}

const StatusIcon = ({ status }: { status: string }) => {
  switch (status) {
    case "pause":
      return <RequestStatusPausedIcon size="xsmall" />;
    case "connector":
      return <RequestStatusCompletedIcon size="xsmall" />;
    case "pencil":
      return <RequestStatusDraftIcon size="xsmall" />;
    case "assigned":
      return <RequestStatusAssignedIcon size="small" />;
    case "clock":
      return <RequestStatusInProgressIcon size="small" />;
    case "tick":
      return <RequestStatusFinishedIcon size="small" />;
    case "cancel":
      return <RequestStatusCancelledIcon size="small" />;
    default:
      return null;
  }
};

const StyledAccordion = styled(Accordion)({
  marginBottom: "12px !important",
  marginTop: "0px !important",
});

const Summary = ({
  title,
  statusText,
  statusIconText,
  isMobile,
}: {
  title: string;
  statusText?: string;
  statusIconText?: string;
  isMobile?: boolean;
}) => {
  return (
    <>
      <Heading variant="h4" weight="medium">
        {title}
      </Heading>
      {statusText && statusIconText && (
        <Box
          display="flex"
          marginTop={isMobile ? { xs: "xsmall", lg: "none" } : undefined}
          style={{ justifySelf: "end" }}
          alignItems="center"
        >
          <Box marginRight="xsmall" display="flex" alignItems="center">
            <StatusIcon status={statusIconText} />
          </Box>
          <Text
            data-id="text"
            component="span"
            align="left"
            truncate={false}
            variant="body-small"
            tone="neutralLight"
          >
            {statusText}
          </Text>
        </Box>
      )}
    </>
  );
};

const FormSection: React.FC<FormSectionProps> = ({
  id,
  title,
  service,
  visible = true,
  children,
  outerRef,
}) => {
  const { setBlocksExpanded, blocksExpanded, formState } =
    React.useContext(FormContext);
  const isBelowMd = useResponsiveBelowMd();
  const state = formState.siteDetails?.state;
  const isServiceLodged =
    service?.requestStatus !== RequestStatusEnum.DRAFT &&
    service?.requestStatus !== RequestStatusEnum.DRAFT_ASSIGNED;
  const statusText =
    isServiceLodged &&
    service?.requestStatus &&
    service?.rawRequestStatus &&
    service?.connectionType &&
    state &&
    service?.messagePause !== undefined
      ? getStatusText(
          service?.requestStatus,
          service?.rawRequestStatus?.toLowerCase(),
          service?.connectionType,
          state,
          "",
          service?.messagePause,
          service?.eicStatus
        )
      : undefined;

  const statusIconText =
    isServiceLodged &&
    service?.requestStatus &&
    service?.messagePause !== undefined &&
    service?.requestType &&
    service?.connectionType
      ? getStatusIconString(
          service?.requestStatus,
          service?.messagePause,
          service?.requestType,
          service?.connectionType
        )
      : undefined;

  if (visible) {
    return (
      <div
        ref={outerRef}
        id={id}
        style={{ scrollMarginTop: pageHeaderHeight }}
        data-role="section"
      >
        {isBelowMd ? (
          <StyledAccordion
            expanded={blocksExpanded[fromHyphenatedToCamelCase(id)]}
            id={id}
            onChange={() => {
              setBlocksExpanded({
                ...blocksExpanded,
                [fromHyphenatedToCamelCase(id)]:
                  !blocksExpanded[fromHyphenatedToCamelCase(id)],
              });
            }}
            summary={
              <Box
                display="flex"
                flexDirection={{
                  xs: "column",
                  lg: "row",
                }}
                style={{ justifySelf: "stretch" }}
              >
                <Summary
                  title={title}
                  statusText={statusText}
                  statusIconText={statusIconText}
                  isMobile={true}
                />
              </Box>
            }
            data-id={`FormSection.isBelowMd.${id}`}
          >
            {children}
          </StyledAccordion>
        ) : (
          <StyledAccordion
            expanded={blocksExpanded[fromHyphenatedToCamelCase(id)]}
            id={id}
            onChange={() => {
              setBlocksExpanded({
                ...blocksExpanded,
                [fromHyphenatedToCamelCase(id)]:
                  !blocksExpanded[fromHyphenatedToCamelCase(id)],
              });
            }}
            summary={
              <Summary
                title={title}
                statusText={statusText}
                statusIconText={statusIconText}
                isMobile={false}
              />
            }
            data-id={`FormSection.not.isBelowMd.${id}`}
          >
            {children}
          </StyledAccordion>
        )}
      </div>
    );
  } else {
    return (
      <div
        ref={outerRef}
        id={id}
        style={{ scrollMarginTop: pageHeaderHeight }}
        data-role="section"
      />
    );
  }
};

export default FormSection;
